import React from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import NotFound from "pages/NotFound";
import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";

// Auth Pages
import LoginPage from "./pages/login";
import OtpSetupPage from "./pages/otp-setup";
import OtpVerifyPage from "./pages/otp-verify";
import SignUpPage from "./pages/sign-up";
import ForgotPasswordPage from "./pages/forgot-password";

// Dashboard
import DashboardPage from "./pages/dashboard";

// Onboarding
import OnboardingPage from "./pages/onboarding";
import OnboardingApprovalPage from "./pages/onboarding-approval";

// Transactions
import TransactionsPage from "./pages/transactions";

// Transaction Detail
import TransactionDetailPage from "./pages/transactions/detail";

// API Keys
import ApiKeysPage from "./pages/api-keys";

// Merchants
import MerchantsPage from "./pages/merchants";

// Users
import UsersPage from "./pages/users";

// RBAC
import RbacPage from "./pages/rbac";

// Audit Logs
import AuditLogsPage from "./pages/audit-logs";

// Payment Operations
import SettlementsPage from "./pages/payment-operations/settlements";
import SettlementDetailPage from "./pages/payment-operations/settlements/detail";
import RefundsPage from "./pages/payment-operations/refunds";
import ChargebacksPage from "./pages/payment-operations/chargebacks";

// Payment Link Management
import PaymentLinkManagementPage from "./pages/payment-link-management";

// Finance
import ChartOfAccountsPage from "./pages/finance/chart-of-accounts";
import AccountTypesPage from "./pages/finance/account-types";
import BalanceSheetPage from "./pages/finance/balance-sheet";
import IncomeStatementPage from "./pages/finance/income-statement";
import TransactionJournalPage from "./pages/finance/transaction-journal";

// Portfolio Management
import PortfolioManagementPage from "./pages/portfolio-management";

// Integration
import ApiDocumentationPage from "./pages/integration/api-documentation";

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <AuthProvider>
          <ScrollToTop />
          <RouterRoutes>
            {/* Public Routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/sign-up" element={<SignUpPage />} />
            <Route path="/otp-setup" element={<OtpSetupPage />} />
            <Route path="/otp-verify" element={<OtpVerifyPage />} />

            {/* Default redirect */}
            <Route path="/" element={<Navigate to="/login" replace />} />

            {/* Protected Routes */}
            <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />

            {/* Onboarding */}
            <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
            <Route path="/onboarding/setup" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
            <Route path="/onboarding/approval" element={<ProtectedRoute><OnboardingApprovalPage /></ProtectedRoute>} />
            <Route path="/onboarding-approval" element={<ProtectedRoute><OnboardingApprovalPage /></ProtectedRoute>} />

            {/* Transactions */}
            <Route path="/transactions/deposits" element={<ProtectedRoute><TransactionsPage paymentType="deposit" /></ProtectedRoute>} />
            <Route path="/transactions/payouts" element={<ProtectedRoute><TransactionsPage paymentType="withdrawal" /></ProtectedRoute>} />
            <Route path="/transactions/detail/:id" element={<ProtectedRoute><TransactionDetailPage /></ProtectedRoute>} />

            {/* API Keys */}
            <Route path="/api-keys" element={<ProtectedRoute><ApiKeysPage /></ProtectedRoute>} />

            {/* Merchants */}
            <Route path="/merchants" element={<ProtectedRoute><MerchantsPage /></ProtectedRoute>} />

            {/* Users */}
            <Route path="/users" element={<ProtectedRoute><UsersPage /></ProtectedRoute>} />

            {/* RBAC */}
            <Route path="/rbac/permission-groups" element={<ProtectedRoute><RbacPage section="permission-groups" /></ProtectedRoute>} />
            <Route path="/rbac/permissions" element={<ProtectedRoute><RbacPage section="permissions" /></ProtectedRoute>} />
            <Route path="/rbac/roles" element={<ProtectedRoute><RbacPage section="roles" /></ProtectedRoute>} />
            <Route path="/rbac/role-permissions" element={<ProtectedRoute><RbacPage section="role-permissions" /></ProtectedRoute>} />
            <Route path="/rbac/user-roles" element={<ProtectedRoute><RbacPage section="user-roles" /></ProtectedRoute>} />

            {/* Audit Logs */}
            <Route path="/audit-logs" element={<ProtectedRoute><AuditLogsPage /></ProtectedRoute>} />

            {/* Payment Operations */}
            <Route path="/payment-operations/settlements" element={<ProtectedRoute><SettlementsPage /></ProtectedRoute>} />
            <Route path="/payment-operations/settlements/:id" element={<ProtectedRoute><SettlementDetailPage /></ProtectedRoute>} />
            <Route path="/payment-operations/refunds" element={<ProtectedRoute><RefundsPage /></ProtectedRoute>} />
            <Route path="/payment-operations/chargebacks" element={<ProtectedRoute><ChargebacksPage /></ProtectedRoute>} />

            {/* Payment Link Management */}
            <Route path="/payment-link-management" element={<ProtectedRoute><PaymentLinkManagementPage /></ProtectedRoute>} />

            {/* Finance */}
            <Route path="/finance/chart-of-accounts" element={<ProtectedRoute><ChartOfAccountsPage /></ProtectedRoute>} />
            <Route path="/finance/account-types" element={<ProtectedRoute><AccountTypesPage /></ProtectedRoute>} />
            <Route path="/finance/balance-sheet" element={<ProtectedRoute><BalanceSheetPage /></ProtectedRoute>} />
            <Route path="/finance/income-statement" element={<ProtectedRoute><IncomeStatementPage /></ProtectedRoute>} />
            <Route path="/finance/transaction-journal" element={<ProtectedRoute><TransactionJournalPage /></ProtectedRoute>} />

            {/* Portfolio Management */}
            <Route path="/portfolio-management" element={<ProtectedRoute><PortfolioManagementPage /></ProtectedRoute>} />

            {/* Integration */}
            <Route path="/integration/api-documentation" element={<ProtectedRoute><ApiDocumentationPage /></ProtectedRoute>} />

            <Route path="*" element={<NotFound />} />
          </RouterRoutes>
        </AuthProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
